import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "../../../assets/css/gamesCss/Graph/introUndirected.css"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";
import { Row } from "reactstrap"


const UndirectedGraphIntro = () => {

    const { selLanguage, toggleLanguage, isLoading } = useLanguage();
    const selectedStage = JSON.parse(localStorage.getItem("selectedStage"));
    var games = selectedStage.games;
    var nameGameKey = localStorage.getItem("gameClicked");
    var selectedGame = null;
    for (let i = 0; i < games.length; i++) {
        if (games[i].game_title === nameGameKey) {
            selectedGame = games[i];
            break;
        }
    }

    const mission = selLanguage ? "Σχεδίασε τον γράφο με βάση τον πίνακα γειτνίασης." : "Draw the graph based on the adjacency matrix";
    const SVG_WIDTH = 700;
    const SVG_HEIGHT = 450;
    const NODE_SIZE = 50;

    // Πίνακας γειτνίασης (Adjacency Matrix) - Συμμετρικός για undirected graph
    const adjacencyMatrix = {
        nodes: ["A", "B", "C", "D"],
        matrix: [
            [0, 1, 1, 0], // A συνδέεται με B, C
            [1, 0, 1, 1], // B συνδέεται με A, C, D
            [1, 1, 0, 1], // C συνδέεται με A, B, D
            [0, 1, 1, 0], // D συνδέεται με B, C
        ],
    };

    // Trap nodes - κόμβοι που δεν υπάρχουν στον πίνακα γειτνίασης
    const trapNodes = ["E", "F"];

    const allInitialNodes = [
        ...adjacencyMatrix.nodes.map((id) => ({ id, label: id, isTrap: false })),
        ...trapNodes.map((id) => ({ id, label: id, isTrap: true })),
    ];

    const [nodes, setNodes] = useState([]);
    const [availableNodes, setAvailableNodes] = useState(allInitialNodes);
    const [edges, setEdges] = useState([]);
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [lives, setLives] = useState(3);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [pathResult, setPathResult] = useState(null);
    const { darkMode } = useDarkMode();
    const [showInstructions, setShowInstructions] = useState(true);


    useEffect(() => {
        if (lives <= 0) {
            setShowGameOver(true);
        }
    }, [lives]);

    const handleDropNode = (nodeId, x, y) => {
        const node = availableNodes.find((n) => n.id === nodeId);
        if (!node) return;

        const pos = {
            x: Math.max(0, Math.min(x - NODE_SIZE / 2, SVG_WIDTH - NODE_SIZE)),
            y: Math.max(0, Math.min(y - NODE_SIZE / 2, SVG_HEIGHT - NODE_SIZE)),
        };
        setNodes((prev) => [...prev, { ...node, ...pos }]);
        setAvailableNodes((prev) => prev.filter((n) => n.id !== nodeId));
    };

    const handleNodeClick = (nodeId) => {
        if (selectedNodes.includes(nodeId)) {
            setSelectedNodes(selectedNodes.filter((id) => id !== nodeId));
        } else if (selectedNodes.length < 2) {
            setSelectedNodes([...selectedNodes, nodeId]);
        }
    };

    const connectNodes = () => {
        if (selectedNodes.length !== 2) return;

        const [from, to] = selectedNodes;
        const edgeExists = edges.find(
            (edge) =>
                (edge.from === from && edge.to === to) ||
                (edge.from === to && edge.to === from)
        );

        if (!edgeExists) {
            setEdges([...edges, { from, to }]);
        }
        setSelectedNodes([]);
    };

    const getNodeById = (id) => nodes.find((n) => n.id === id);

    const checkGraph = async () => {
        setIsChecking(true);
        setPathResult(null);

        await new Promise((resolve) => setTimeout(resolve, 500));

        const placedNodeIds = nodes.map((n) => n.id);
        const correctNodeIds = adjacencyMatrix.nodes;

        // Έλεγχος για trap nodes
        const hasTrapNodes = nodes.some((n) => n.isTrap);
        if (hasTrapNodes) {
            setPathResult({
                success: false,
                message: selLanguage
                    ? "Υπάρχουν λάθος κόμβοι στο γράφο! Οι κόμβοι που χρησιμοποιήθηκαν δεν ανήκουν στον πίνακα γειτνίασης."
                    : "There are incorrect nodes in the graph! The nodes used do not belong to the adjacency matrix.",
            });
            setLives((prev) => prev - 1);
            setIsChecking(false);
            return;
        }

        // Έλεγχος αν υπάρχουν όλοι οι σωστοί κόμβοι
        const missingNodes = correctNodeIds.filter((id) => !placedNodeIds.includes(id));
        if (missingNodes.length > 0) {
            setPathResult({
                success: false,
                message: selLanguage
                    ? `Λείπουν κόμβοι από το γράφο!`
                    : `Missing nodes from the graph!`,
            });
            setLives((prev) => prev - 1);
            setIsChecking(false);
            return;
        }

        // Δημιουργία adjacency matrix από τις ακμές του χρήστη
        const userMatrix = {};
        correctNodeIds.forEach((node) => {
            userMatrix[node] = {};
            correctNodeIds.forEach((otherNode) => {
                userMatrix[node][otherNode] = 0;
            });
        });

        edges.forEach((edge) => {
            if (userMatrix[edge.from] && userMatrix[edge.to]) {
                userMatrix[edge.from][edge.to] = 1;
                userMatrix[edge.to][edge.from] = 1; // Undirected
            }
        });

        // Έλεγχος αν ο πίνακας γειτνίασης του χρήστη ταιριάζει με τον σωστό
        let isCorrect = true;
        for (let i = 0; i < correctNodeIds.length; i++) {
            for (let j = 0; j < correctNodeIds.length; j++) {
                const nodeI = correctNodeIds[i];
                const nodeJ = correctNodeIds[j];
                if (userMatrix[nodeI][nodeJ] !== adjacencyMatrix.matrix[i][j]) {
                    isCorrect = false;
                    break;
                }
            }
            if (!isCorrect) break;
        }

        if (isCorrect) {
            setShowGameWon(true);
        } else {
            setPathResult({
                success: false,
                message: selLanguage
                    ? "Οι συνδέσεις δεν ταιριάζουν με τον πίνακα γειτνίασης! Έλεγξε τις ακμές σου."
                    : "The connections do not match the adjacency matrix! Check your edges.",
            });
            setLives((prev) => prev - 1);
        }

        setIsChecking(false);
    };

    const removeNodeFromFrame = (nodeId) => {
        const nodeToRemove = nodes.find((n) => n.id === nodeId);
        if (!nodeToRemove) return;

        setNodes((prev) => prev.filter((n) => n.id !== nodeId));
        setAvailableNodes((prev) => [...prev, { id: nodeToRemove.id, label: nodeToRemove.label, isTrap: nodeToRemove.isTrap }]);
        setEdges((prev) => prev.filter((edge) => edge.from !== nodeId && edge.to !== nodeId));
        setSelectedNodes((prev) => prev.filter((id) => id !== nodeId));
    };

    const handleDragStartAvailable = (e, nodeId) => {
        e.dataTransfer.setData("nodeId", nodeId);
    };

    const handleDragOverFrame = (e) => e.preventDefault();

    const handleDropOnFrame = (e) => {
        e.preventDefault();
        const nodeId = e.dataTransfer.getData("nodeId");
        if (nodeId) {
            const svgElement = e.currentTarget.querySelector("svg");
            const rect = svgElement.getBoundingClientRect();
            const scaleX = SVG_WIDTH / rect.width;
            const scaleY = SVG_HEIGHT / rect.height;

            const x = (e.clientX - rect.left) * scaleX;
            const y = (e.clientY - rect.top) * scaleY;

            handleDropNode(nodeId, x, y);
        }
    };

    const resetGame = () => {
        setNodes([]);
        setAvailableNodes(allInitialNodes);
        setEdges([]);
        setSelectedNodes([]);
        setLives(3);
        setShowGameOver(false);
        setShowGameWon(false);
        setPathResult(null);
    };

    return (
        <>

            <div
                className="main-content introUndirectedContainer"
                style={{
                    background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)"
                }}
            >
                <GameOverModal isVisible={showGameOver} onPlayAgain={resetGame} />
                <GameWonModal isVisible={showGameWon} onPlayAgain={resetGame} />

                <div style={{ width: "100%", maxWidth: "770px" }}>
                    {/* Lives Display */}
                    <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>

                            <div style={{ display: "flex", gap: "6px" }}>
                                {[1, 2, 3].map((heartNum) => (
                                    <div
                                        key={heartNum}
                                        style={{
                                            fontSize: "24px",
                                            filter: lives >= heartNum ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))" : "grayscale(100%) opacity(0.3)",
                                            animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none",
                                        }}
                                    >
                                        ❤️
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>




                    <div className="introUndirectedProm">
                        {mission}
                    </div>

                    {/* 
                        <div
                        className="info-panel"
                        style={{
                            display: "flex",
                            gap: "20px",
                            maxWidth: "770px",
                            alignItems: "flex-start",

                        }}
                    >
                        
                        <div
                            className="info-panel-header"
                            onClick={() => setShowInstructions(!showInstructions)}
                            style={{
                                flex: "0 0 auto",
                                minWidth: "200px"
                            }}
                        >
                            <span>📖 {selLanguage ? "Οδηγίες" : "Instructions"}</span>
                            <span className="toggle-icon">{showInstructions ? "▼" : "▶"}</span>
                        </div>


                        {showInstructions && (
                            <div
                                className="info-panel-content"
                                style={{
                                    flex: "1",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px"
                                }}
                            >
                                <div className="instruction-item">

                                    <span style={{ color: darkMode ? "#fff" : "#1a1919ff" }}>
                                        {selLanguage ? "Σύρε τους κόμβους στο πλαίσιο" : "Drag the nodes"}
                                    </span>
                                </div>
                                <div className="instruction-item">

                                    <span style={{ color: darkMode ? "#fff" : "#1a1919ff" }}>
                                        {selLanguage ? "Κλικ σε 2 κόμβους → Σύνδεση" : "Click 2 nodes → Connect"}
                                    </span>
                                </div>
                                <div className="instruction-item">

                                    <span style={{ color: darkMode ? "#fff" : "#1a1919ff" }}>
                                        {selLanguage ? "Double-click για αφαίρεση κόμβου" : "Double-click to remove node"}
                                    </span>
                                </div>
                            </div>
                        )} 
                </div>*/}


                    {/* Adjacency Matrix */}

                    <div className="adjacency-matrix">
                        <div className="matrix-title"> {selLanguage ? "Πίνακας Γειτνίασης" : "Adjacency Matrix"}</div>
                        <div
                            className="matrix-grid"
                            style={{
                                gridTemplateColumns: `repeat(${adjacencyMatrix.nodes.length + 1}, 40px)`,
                            }}
                        >
                            <div className="matrix-cell"></div>
                            {adjacencyMatrix.nodes.map((node) => (
                                <div key={node} className="matrix-cell matrix-header">
                                    {node}
                                </div>
                            ))}
                            {adjacencyMatrix.nodes.map((rowNode, i) => (
                                <React.Fragment key={rowNode}>
                                    <div className="matrix-cell matrix-header">{rowNode}</div>
                                    {adjacencyMatrix.matrix[i].map((value, j) => (
                                        <div
                                            key={j}
                                            className={`matrix-cell matrix-value ${value === 1 ? "connected" : ""}`}
                                        >
                                            {value}
                                        </div>
                                    ))}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Available Nodes */}
                    <div className="introUndirectedLeftNodes">
                        {availableNodes.map((node) => (
                            <div
                                key={node.id}
                                draggable
                                onDragStart={(e) => handleDragStartAvailable(e, node.id)}
                                className={`introUndirectedNode`}
                                title={
                                    node.isTrap
                                        ? (selLanguage ? "⚠️ Παγίδα!" : "⚠️ Trap!")
                                        : (selLanguage ? `${node.label} - Σύρε στο πλαίσιο` : `${node.label} - Drag to frame`)
                                }
                            >
                                {node.id}
                            </div>
                        ))}
                    </div>

                    {/* SVG Frame */}
                    <div
                        className={`introUndirectedFrame ${isChecking ? "checking-animation" : ""}`}
                        onDragOver={handleDragOverFrame}
                        onDrop={handleDropOnFrame}
                    >
                        <svg width="100%" height="auto" viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} style={{ display: "block" }}>
                            <defs>
                                <filter id="glow">
                                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                    <feMerge>
                                        <feMergeNode in="coloredBlur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>

                            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e2e8f0" strokeWidth="1" opacity="0.3" />
                            </pattern>
                            <rect width="100%" height="100%" fill="url(#grid)" />

                            {/* Edges */}
                            {edges.map((edge, idx) => {
                                const from = getNodeById(edge.from);
                                const to = getNodeById(edge.to);
                                if (!from || !to) return null;

                                const fromCenterX = from.x + NODE_SIZE / 2;
                                const fromCenterY = from.y + NODE_SIZE / 2;
                                const toCenterX = to.x + NODE_SIZE / 2;
                                const toCenterY = to.y + NODE_SIZE / 2;

                                const dx = toCenterX - fromCenterX;
                                const dy = toCenterY - fromCenterY;
                                const distance = Math.sqrt(dx * dx + dy * dy);
                                const unitX = dx / distance;
                                const unitY = dy / distance;
                                const radius = NODE_SIZE / 2;

                                const startX = fromCenterX + unitX * radius;
                                const startY = fromCenterY + unitY * radius;
                                const endX = toCenterX - unitX * radius;
                                const endY = toCenterY - unitY * radius;

                                return (
                                    <line
                                        key={idx}
                                        x1={startX}
                                        y1={startY}
                                        x2={endX}
                                        y2={endY}
                                        stroke="#3b82f6"
                                        strokeWidth="4"
                                        filter="url(#glow)"
                                        opacity="0.8"
                                    />
                                );
                            })}

                            {/* Nodes */}
                            {nodes.map((node) => (
                                <g key={node.id}>
                                    <motion.circle
                                        cx={node.x + NODE_SIZE / 2 + 2}
                                        cy={node.y + NODE_SIZE / 2 + 2}
                                        r={NODE_SIZE / 2 - 2}
                                        fill="rgba(0,0,0,0.2)"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                    />
                                    <motion.circle
                                        cx={node.x + NODE_SIZE / 2}
                                        cy={node.y + NODE_SIZE / 2}
                                        r={NODE_SIZE / 2 - 2}
                                        fill={selectedNodes.includes(node.id) ? "#f97316" : "#3b82f6"}
                                        stroke="#1f2937"
                                        strokeWidth="3"
                                        style={{ cursor: "pointer" }}
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        whileHover={{ scale: 1.15 }}
                                        whileTap={{ scale: 0.95 }}
                                        onMouseDown={() => handleNodeClick(node.id)}
                                        onDoubleClick={() => removeNodeFromFrame(node.id)}
                                    />
                                    <motion.text
                                        x={node.x + NODE_SIZE / 2}
                                        y={node.y + NODE_SIZE / 2 + 5}
                                        textAnchor="middle"
                                        fill="white"
                                        fontSize="18"
                                        fontWeight="700"
                                        style={{ pointerEvents: "none", userSelect: "none" }}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                    >
                                        {node.id}
                                    </motion.text>
                                </g>
                            ))}
                        </svg>
                    </div>

                    {/* Control Buttons */}
                    <div style={{ textAlign: "center", display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
                        <button
                            onClick={connectNodes}
                            disabled={selectedNodes.length !== 2 || isChecking}
                            style={{
                                background: selectedNodes.length === 2 && !isChecking ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "linear-gradient(135deg, #64748b 0%, #475569 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                cursor: selectedNodes.length === 2 && !isChecking ? "pointer" : "not-allowed",
                                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                                transition: "all 0.3s ease",
                            }}
                        >
                            {selLanguage ? "Σύνδεση" : "Connect"}({selectedNodes.length} / 2)
                        </button>

                        <button
                            onClick={checkGraph}
                            disabled={isChecking}
                            style={{
                                background: isChecking ? "linear-gradient(135deg, #64748b 0%, #475569 100%)" : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                cursor: isChecking ? "not-allowed" : "pointer",
                                boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3)",
                                transition: "all 0.3s ease",
                            }}
                        >
                            {isChecking ? (selLanguage ? "Ελέγχεται..." : "Checking...") : (selLanguage ? "Έλεγχος Γράφου" : "Graph Check")}
                        </button>

                        <button
                            onClick={resetGame}
                            style={{
                                background: "linear-gradient(135deg, #b81e1eff 0%, #7c0505ff 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                boxShadow: "0 10px 25px rgba(184, 30, 30, 0.3)",
                                transition: "all 0.3s ease",
                            }}
                        >
                            {selLanguage ? "Καθαρισμός" : "Restart"}
                        </button>
                    </div>

                    {/* Result Message */}
                    {pathResult && (
                        <div
                            style={{
                                textAlign: "center",
                                padding: "20px",
                                borderRadius: "16px",
                                marginTop: "20px",
                                background: pathResult.success ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)" : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                                border: `2px solid ${pathResult.success ? "#10b981" : "#ef4444"}`,
                                color: pathResult.success ? "#059669" : "#dc2626",
                                fontWeight: 600,
                                fontSize: "1rem",
                            }}
                        >
                            {pathResult.message}
                        </div>
                    )}
                </div>
            </div >
        </>
    );
};

export default UndirectedGraphIntro;